import type { FC } from 'react'

import { useCallback, useEffect, useRef, useState } from 'react'
import { Check, RotateCcw, ShieldCheck } from 'lucide-react'
import { cn } from '@/lib/utils'
import { randomBase58 } from '@/lib/random'
import { randomBytes, sha256, shorten, toHex } from '@/lib/crypto'
import { useInView } from '@/hooks/use-in-view'
import { useReducedMotion } from '@/hooks/use-reduced-motion'
import ScrambleText from '@/components/landing/scramble-text'
import { FAIRNESS_STEPS } from '@/constants/kinematic'

type StepStatus = 'idle' | 'running' | 'done'

type Result = {
    roll: number
    win: boolean
}

const TARGET = 50
const SAMPLE_RESULT: Result = { roll: 73.42, win: true }

const StatusDot: FC<{ status: StepStatus, index: number }> = ({ status, index }) => {
    if (status === 'done') {
        return (
            <span className='grid size-8 animate-in zoom-in-50 place-items-center rounded-full bg-mint text-ink duration-300'>
                <Check aria-hidden='true' strokeWidth={2.5} className='size-4' />
            </span>
        )
    }

    if (status === 'running') {
        return (
            <span className='relative grid size-8 place-items-center'>
                <span className='absolute inset-0 animate-spin rounded-full border-2 border-ember/25 border-t-ember' />
                <span className='font-mono text-[10px] text-ember'>{index + 1}</span>
            </span>
        )
    }

    return (
        <span className='grid size-8 place-items-center rounded-full border border-white/12 font-mono text-[10px] text-steel'>
            {index + 1}
        </span>
    )
}

const FairnessConsole: FC = () => {
    const reduced = useReducedMotion()
    const { ref, inView } = useInView<HTMLDivElement>('-10% 0px', true)
    const [statuses, setStatuses] = useState<StepStatus[]>(() => FAIRNESS_STEPS.map(() => 'done'))
    const [values, setValues] = useState<string[]>(() => FAIRNESS_STEPS.map(step => step.sample))
    const [result, setResult] = useState<Result | null>(SAMPLE_RESULT)
    const [betId, setBetId] = useState(41230)
    const [running, setRunning] = useState(false)
    const runToken = useRef(0)
    const mounted = useRef(false)

    useEffect(() => {
        mounted.current = true

        return () => {
            mounted.current = false
        }
    }, [])

    const run = useCallback(async () => {
        const token = runToken.current + 1
        runToken.current = token

        const alive = () => mounted.current && runToken.current === token
        const pause = (ms: number) => new Promise<void>(resolve => window.setTimeout(resolve, reduced ? 0 : ms))

        setRunning(true)
        setResult(null)
        setStatuses(FAIRNESS_STEPS.map(() => 'idle'))
        setValues(FAIRNESS_STEPS.map(() => ''))
        setBetId(previous => previous + 1)

        const seed = randomBytes(32)
        const proof = randomBytes(48)
        const commitment = await sha256(seed)

        const outputs = [
            shorten(commitment),
            shorten(toHex(randomBytes(64))),
            shorten(toHex(randomBytes(64))),
            `π ${shorten(toHex(proof), 8, 6)}`,
            '4 of 4 checks passed',
            `devnet · ${randomBase58(6)}…${randomBase58(4)}`
        ]

        for (let index = 0; index < outputs.length; index++) {
            await pause(index === 0 ? 250 : 140)
            if (!alive()) return

            setStatuses(previous => previous.map((status, i) => (i === index ? 'running' : status)))

            await pause(index === 4 ? 780 : 520)
            if (!alive()) return

            setValues(previous => previous.map((value, i) => (i === index ? outputs[index] : value)))
            setStatuses(previous => previous.map((status, i) => (i === index ? 'done' : status)))
        }

        const combined = new Uint8Array(seed.length + proof.length)
        combined.set(seed)
        combined.set(proof, seed.length)

        const outcome = await sha256(combined)
        if (!alive()) return

        const roll = (parseInt(outcome.slice(0, 8), 16) % 10000) / 100

        setResult({ roll, win: roll > TARGET })
        setRunning(false)
    }, [reduced])

    useEffect(() => {
        if (inView && !reduced) void run()
    }, [inView, reduced, run])

    return (
        <div ref={ref} className='overflow-hidden rounded-[28px] border border-white/10 bg-ink-1 shadow-[0_60px_140px_-70px_rgba(75,227,160,0.35)]'>
            <div className='flex h-12 items-center justify-between gap-3 border-b border-white/[0.06] px-5'>
                <span className='flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.14em] text-bone'>
                    <ShieldCheck aria-hidden='true' className='size-4 text-mint' />
                    Bet #{betId}
                </span>
                <span className='font-mono text-[10px] uppercase tracking-[0.14em] text-steel'>
                    Demo, real SHA-256
                </span>
            </div>

            <dl className='grid grid-cols-2 gap-px border-b border-white/[0.06] bg-white/[0.06] sm:grid-cols-4'>
                {[
                    { label: 'Game', value: 'Dice' },
                    { label: 'Target', value: 'Over 50.00' },
                    { label: 'Payout', value: '×1.98' },
                    { label: 'Stake', value: '10.00 TEST' }
                ].map(item => (
                    <div key={item.label} className='bg-ink-1 px-5 py-3.5'>
                        <dt className='font-mono text-[9px] uppercase tracking-[0.18em] text-steel'>{item.label}</dt>
                        <dd className='mt-1 font-mono text-xs text-bone'>{item.value}</dd>
                    </div>
                ))}
            </dl>

            <ol>
                {FAIRNESS_STEPS.map((step, index) => {
                    const status = statuses[index]

                    return (
                        <li
                            key={step.key}
                            className={cn(
                                'grid grid-cols-[32px_minmax(0,1fr)] gap-4 border-b border-white/[0.05] px-5 py-4 transition-colors duration-300',
                                status === 'running' && 'bg-ember/[0.04]'
                            )}
                        >
                            <StatusDot status={status} index={index} />

                            <div className='min-w-0'>
                                <div className='flex flex-wrap items-center gap-x-3 gap-y-1.5'>
                                    <span className='text-[15px] font-medium text-bone'>{step.title}</span>
                                    <span className='rounded-md border border-white/10 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.14em] text-steel'>
                                        {step.tech}
                                    </span>
                                    <span className={cn('min-w-0 truncate font-mono text-[11px] sm:ml-auto', index === 4 ? 'text-mint' : index === 3 ? 'text-ember-soft' : 'text-bone/75')}>
                                        <ScrambleText value={values[index]} />
                                    </span>
                                </div>
                                <p className='mt-1 text-sm leading-relaxed text-steel'>{step.text}</p>
                            </div>
                        </li>
                    )
                })}
            </ol>

            <div className='flex flex-col gap-4 bg-white/[0.02] px-5 py-5 sm:flex-row sm:items-center sm:justify-between'>
                <div className='flex items-center gap-4'>
                    <div>
                        <div className='font-mono text-[9px] uppercase tracking-[0.18em] text-steel'>Roll</div>
                        <div className={cn('mt-1 font-mono text-3xl tabular-nums tracking-tight', result ? (result.win ? 'text-mint' : 'text-bone/50') : 'text-steel/40')}>
                            {result ? result.roll.toFixed(2) : '··.··'}
                        </div>
                    </div>

                    <span
                        className={cn(
                            'rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.12em]',
                            !result && 'border-white/10 text-steel',
                            result?.win && 'border-mint/40 bg-mint/10 text-mint',
                            result && !result.win && 'border-white/10 text-steel'
                        )}
                    >
                        {!result ? 'Checking' : result.win ? 'Win ×1.98, verified' : 'No win, verified'}
                    </span>
                </div>

                <button
                    type='button'
                    onClick={() => void run()}
                    disabled={running}
                    className='inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-bone px-4 text-sm font-medium text-ink transition-colors hover:bg-white disabled:cursor-wait disabled:opacity-60'
                >
                    <RotateCcw aria-hidden='true' className={cn('size-4', running && 'animate-spin')} />
                    {running ? 'Verifying' : 'Roll another bet'}
                </button>
            </div>
        </div>
    )
}

export default FairnessConsole
